import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Der Container, auf dem der Variant-Picker sein ProductSelectEvent feuert.
 * Identisch zu product-sku.js, damit der Block sowohl auf der PDP als auch
 * im Quick-Add und in Produktkarten funktioniert.
 */
const TARGET_SELECTOR = '[id*="ProductInformation-"], [id*="QuickAdd-"], product-card';
const MAP_SELECTOR = 'script[type="application/json"][ref="titleMap"]';

/**
 * Zeigt einen variantenabhaengigen Titel an.
 *
 * Datenquelle ist primaer die serverseitig gerenderte Titel-Map (Varianten-ID
 * -> fertiger Text) im Daten-Island. Das haelt die gesamte Textlogik in Liquid
 * (Metafelder, Freitext-Vorlage) und braucht clientseitig nur einen Lookup.
 * Faellt zurueck auf das mitgelieferte Section-HTML, wenn die Map nicht greift
 * (unavailable Kombination, Combined Listing / anderes Produkt).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} titleText - Das Element, das den Titeltext traegt.
 *
 * @extends {Component<Refs>}
 */
class GlrVariantTitle extends Component {
  requiredRefs = ['titleText'];

  /** @type {Element | null} */
  #listenTarget = null;

  /** @type {Record<string, string> | null} */
  #titleMap = null;

  connectedCallback() {
    super.connectedCallback();
    // Ziel merken: in disconnectedCallback liefert closest() bereits null.
    this.#listenTarget = this.closest(TARGET_SELECTOR);
    this.#listenTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#listenTarget = null;
  }

  /**
   * Liest (und cached) die serverseitig gerenderte Titel-Map.
   * @returns {Record<string, string>}
   */
  #titles() {
    if (this.#titleMap) return this.#titleMap;

    const script = this.querySelector(MAP_SELECTOR);

    try {
      const parsed = JSON.parse(script?.textContent || '{}');
      this.#titleMap = parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('[glr-variant-title] Titel-Map konnte nicht gelesen werden:', error);
      this.#titleMap = {};
    }

    return this.#titleMap;
  }

  /**
   * Setzt den Titeltext. Die Map enthaelt bewusst reinen Text; das hervorgehobene
   * Zeichen wird hier als DOM aufgebaut, nie ueber innerHTML.
   * @param {string | undefined} text
   * @returns {boolean} true, wenn ein gueltiger Text gesetzt wurde.
   */
  #setText(text) {
    if (typeof text !== 'string' || text.trim() === '') return false;

    const { titleText } = this.refs;
    if (!titleText) return false;

    const mark = this.dataset.mark || '';

    // Ohne Hervorhebung: textContent - schnell und ohne HTML.
    if (!mark || !text.includes(mark)) {
      if (titleText.textContent !== text) titleText.textContent = text;
      return true;
    }

    // Mit Hervorhebung: DOM gezielt aufbauen. Bewusst KEIN innerHTML -
    // der Text stammt aus Customizer-Settings und darf nie als Markup gelten.
    if (titleText.textContent === text && titleText.querySelector('.glr-variant-title__mark')) return true;

    const frag = document.createDocumentFragment();
    const parts = text.split(mark);
    parts.forEach((part, i) => {
      if (part) frag.appendChild(document.createTextNode(part));
      if (i < parts.length - 1) {
        const span = document.createElement('span');
        span.className = 'glr-variant-title__mark';
        span.textContent = mark;
        frag.appendChild(span);
      }
    });
    titleText.replaceChildren(frag);

    return true;
  }


  /**
   * Fallback: liest den eigenen, frisch gerenderten Block aus dem
   * nachgeladenen Section-HTML und uebernimmt Text + Map.
   * @param {Document | undefined} html
   * @returns {boolean}
   */
  #applyFromHtml(html) {
    if (!html) return false;

    const source = html.querySelector(`glr-variant-title[data-block-id="${this.dataset.blockId}"]`);
    if (!source) return false;

    const newMap = source.querySelector(MAP_SELECTOR);
    const ownMap = this.querySelector(MAP_SELECTOR);
    if (newMap && ownMap) {
      ownMap.textContent = newMap.textContent;
      this.#titleMap = null;
    }

    return this.#setText(source.querySelector('[ref="titleText"]')?.textContent?.trim());
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Events aus verschachtelten Produktkarten (z. B. Empfehlungen) ignorieren.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { resource, newProduct, html } = detail;

        // Combined Listing: der Picker hat auf ein ANDERES Produkt gewechselt.
        // Unsere Map gehoert dann zum alten Produkt -> aus dem HTML nachziehen.
        if (newProduct) {
          this.dataset.productId = newProduct.id;
          this.#titleMap = null;
          this.#applyFromHtml(html);
          return;
        }

        if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        const variantId = resource?.id;
        if (variantId != null && this.#setText(this.#titles()[String(variantId)])) return;

        // resource === null (nicht verfuegbare Kombination) oder ID nicht in der Map.
        this.#applyFromHtml(html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[glr-variant-title] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('glr-variant-title')) {
  customElements.define('glr-variant-title', GlrVariantTitle);
}
